import React, { useCallback, useMemo, useRef, useState } from 'react';
import { exportComponentAsJPEG } from 'react-component-export-image';
import { QRCode } from 'react-qrcode-logo';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, ButtonCopyToClipboard, FieldWrapper, H2, Icon, Input, ModalContent, ModalFooter, ModalForm, ModalFormLayout, ModalHeader, ModalLayout, ModalText, ModalTitle, Text } from 'components';
import { FailToastContainer } from 'components/Toast/ToastContainer';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import FullLogoImage from 'images/illustrations/FullLogo.svg';
import { PermissionGate } from 'screens/Security';
import { EmployeeRole, IEmployee, ISiteAcceptanceSettings } from 'types';

const StyledModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(14)};

  svg {
    margin-bottom: ${toRem(26)};
  }
`;

const StyledModalTitle = styled(ModalTitle)`
  font-size: ${toRem(20)};
`;

const StyledModalFooter = styled(ModalFooter)`
  @media (max-width: ${theme.breakpoint.small}px) {
    .download-button {
      display: none;
    }
  }
`;

const CardFront = styled.div`
  flex-flow: row wrap;
  justify-content: space-around;
  align-items: center;
  background-color: ${theme.color.neutral[50]};
  width: 100%;
  border-radius: ${toRem(theme.borderRadius.small)};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding: ${toRem(36)} ${toRem(26)};
  margin-bottom: ${toRem(36)};
`;

const CardInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  text-align: center;

  h2 {
    margin-bottom: ${toRem(14)};
  }

  p {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(14)};
    max-width: ${toRem(190)};
    word-wrap: break-word;
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    margin-bottom: ${toRem(18)};
  }
`;

const CardTitle = styled(H2)`
  font-size: ${toRem(13)};
  font-weight: ${theme.font.weight.regular};
  line-height: 143%;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.color.secondary[400]};
`;

const EmployeeName = styled(H2)`
  font-size: ${toRem(26)};
  max-width: ${toRem(190)};
  word-wrap: break-word;
  font-weight: ${theme.font.weight.semiBold};
`;

const SiteName = styled(Text)`
  font-size: ${toRem(13)};
  font-weight: ${theme.font.weight.regular};
  line-height: 143%;
  color: ${theme.color.secondary[300]};
`;

const FormRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;

  button {
    margin-left: ${toRem(16)};

    &:disabled {
      background-color: ${theme.color.neutral[200]};
      color: ${theme.color.neutral[400]};
    }
  }
`;

const CardBack = styled.div`
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${theme.color.neutral[800]};
  width: 100%;
  border-radius: ${toRem(theme.borderRadius.small)};

  p {
    max-width: ${toRem(340)};
    word-wrap: break-word;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
`;

const HR = styled.hr`
  width: 12%;
  border: 1px solid ${theme.color.neutral[700]};
  margin: ${toRem(24)} 0;
`;

const ComponentToPrint = styled.div`
  position: absolute;
  top: ${toRem(-1000)};
  width: 531px;
  height: 286px;

  div:first-child {
    margin: 0;
  }
`;

const acceptanceBaseUrl = process.env.REACT_APP_ACCEPTANCE_BASE_URL;

interface ITipAcceptanceCodeProps {
  employee?: IEmployee;
  siteId?: string;
  siteAcceptance?: ISiteAcceptanceSettings;
  isLoading?: boolean;
  updateDisplayName: (name: string) => void;
  onClose: () => void;
}

const TipAcceptanceCode = ({ employee, siteId, siteAcceptance, isLoading, updateDisplayName, onClose }: ITipAcceptanceCodeProps) => {
  const userSite = siteId && employee?.sites.find((site) => site.id === siteId);

  const [download, setDownload] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);

  const onDownload = useCallback(async () => {
    if (!frontRef.current || !backRef.current) {
      return;
    }

    try {
      setIsDownloadLoading(true);

      const name = `${employee.firstName}_${employee.lastName}`;
      await exportComponentAsJPEG(frontRef, { fileName: `${name}_Front`, html2CanvasOptions: { scrollY: 0 } });
      await exportComponentAsJPEG(backRef, { fileName: `${name}_Back`, html2CanvasOptions: { scrollY: 0 } });

      setDownload(true);
    } catch (err) {
      toast(
        FailToastContainer({
          message: 'Sorry, there was an issue downloading your images. If this problem continues, please contact Kickfin Support.',
        })
      );
      setIsDownloadLoading(false);
    }
  }, [frontRef, backRef, employee]);

  const validationSchema = Yup.object().shape({
    employeeDisplayName: Yup.string().trim().max(30, 'Display name must be 30 characters or less').required('Display name is required'),
  });

  const { dirty, errors, getFieldProps, handleSubmit, touched } = useFormik({
    initialValues: {
      employeeDisplayName: userSite?.employeeDisplayName || '',
    },
    onSubmit: (values) => {
      updateDisplayName(values.employeeDisplayName);
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const validateField = (field: 'employeeDisplayName') => Boolean(touched[field] && errors[field]);

  const acceptanceUrl = userSite?.acceptance && `${acceptanceBaseUrl}/${userSite.acceptance.code}`;

  const cardFront = useMemo(
    () =>
      acceptanceUrl && (
        <CardFront data-cy="card-content">
          <CardInfo>
            <CardTitle>Scan to leave a tip</CardTitle>
            <EmployeeName>{userSite.employeeDisplayName || employee.firstName}</EmployeeName>
            <Text>{siteAcceptance?.displayName || userSite.name}</Text>
          </CardInfo>
          <QRCode value={acceptanceUrl} size={190} logoImage="/logo192.png" logoWidth={26} logoHeight={26} removeQrCodeBehindLogo bgColor={theme.color.neutral[50]} />
        </CardFront>
      ),
    [acceptanceUrl, userSite, siteAcceptance, employee]
  );

  const cardBack = useMemo(
    () =>
      acceptanceUrl && (
        <CardBack>
          <img alt="kickfin" src={FullLogoImage} />
          <HR />
          <SiteName>{siteAcceptance?.displayName || userSite.name}</SiteName>
        </CardBack>
      ),
    [acceptanceUrl, userSite, siteAcceptance]
  );

  return (
    <>
      <ComponentToPrint ref={frontRef}>{cardFront}</ComponentToPrint>

      <ComponentToPrint ref={backRef}>{cardBack}</ComponentToPrint>

      <ModalLayout maxWidth={download ? 420 : 560} isActive onClose={onClose}>
        {download ? (
          <>
            <StyledModalHeader>
              <Icon icon="Export" fill={theme.color.secondary[400]} width={48} height={54} />
              <StyledModalTitle data-cy="modal-title">Download in progress!</StyledModalTitle>
            </StyledModalHeader>
            <ModalContent>
              <ModalText>There will be two images labeled Front and Back that can be uploaded to the Vistaprint business card printing service.</ModalText>
              <ModalFooter columns={1}>
                <StyledLink href={appConfig.LINKS.VISTA_PRINT} target="_blank" rel="noopener noreferrer">
                  <Button appearance="primary" size="large" label="Print Cards" />
                </StyledLink>
              </ModalFooter>
            </ModalContent>
          </>
        ) : (
          <>
            <ModalHeader>
              <StyledModalTitle>QR Code Information</StyledModalTitle>
            </ModalHeader>

            <ModalContent>
              {cardFront}

              <PermissionGate requiredRoles={[EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]}>
                <ModalForm onSubmit={handleSubmit}>
                  <ModalFormLayout>
                    <FieldWrapper label="Display Name" message={validateField('employeeDisplayName') ? errors.employeeDisplayName : ''} hasError={validateField('employeeDisplayName')}>
                      <FormRow>
                        <Input data-cy="display-name-input" id="display-name" type="text" {...getFieldProps('employeeDisplayName')} />
                        <Button data-cy="display-name-button" type="submit" appearance="primary" size="large" label="Save" disabled={!dirty || isLoading} isProcessing={isLoading} />
                      </FormRow>
                    </FieldWrapper>
                  </ModalFormLayout>
                </ModalForm>
              </PermissionGate>

              <StyledModalFooter columns={2}>
                <ButtonCopyToClipboard value={acceptanceUrl} copyLabel="Copy URL" copiedLabel="URL Copied!" interval={5000} />
                <Button className="download-button" appearance="white" size="large" label="Download JPG" onClick={onDownload} disabled={isDownloadLoading} isProcessing={isDownloadLoading} />
              </StyledModalFooter>
            </ModalContent>
          </>
        )}
      </ModalLayout>
    </>
  );
};

export default TipAcceptanceCode;
